/** @jsxImportSource theme-ui */
import { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { selectUIVersion } from "store/app";
import { Box, Button as ThemeUiButton } from "theme-ui";
import { motion, AnimatePresence } from "framer-motion";
import FocusLock from "react-focus-lock";
import debounce from "lodash/debounce";

import { CloseIcon } from "components/icons";
import * as styles from "./ModalCenter.styles";

export default function ModalCenter({
  isTopMostModal,
  maxWidthOverride,
  onClose = () => {},
  children,
}) {
  const [isOpened, setIsOpened] = useState(false);
  const [isVisible, setIsVisible] = useState(true);
  const close = () => setIsVisible(false);

  const UIVersion = useSelector(selectUIVersion);

  useEffect(() => {
    function handleKeyDown(e) {
      if (e.key === "Escape" && isTopMostModal) {
        close();
      }
    }

    const handleKeyDownDebounced = debounce(handleKeyDown);

    document.addEventListener("keydown", handleKeyDownDebounced);
    return () => {
      document.removeEventListener("keydown", handleKeyDownDebounced);
    };
  }, [isTopMostModal]);

  return (
    <AnimatePresence>
      {isVisible && (
        <FocusLock returnFocus disabled={!isOpened}>
          <motion.div
            sx={styles.modalContainer}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
            onAnimationComplete={(definition) => {
              if (definition.opacity === 1) {
                setIsOpened(true);
              }
              if (definition.opacity === 0) {
                onClose();
              }
            }}
          >
            <Box sx={styles.centeredBox({ UIVersion, maxWidthOverride })}>
              <ThemeUiButton
                variant="buttonIconOnlySmall"
                sx={styles.closeButton}
                onClick={close}
              >
                <CloseIcon />
              </ThemeUiButton>

              {typeof children === "function"
                ? children({
                    close,
                  })
                : children}
            </Box>
          </motion.div>
        </FocusLock>
      )}
    </AnimatePresence>
  );
}
